// Case study editorial template.
//
// Pure, browser-safe builder that turns published levels into factual
// search metadata and a five-part post-execution review. It only restates
// recorded facts (levels, timing, outcome) and never exposes engine
// thresholds or filters. Wording avoids promises and advice language.
// Listed in source-tree-manifest.json so the frozen build copies it.

import type { CaseStudySummary } from "./case-study.functions";

export const SITE = "https://xauproacademy.com";

const fmt = (n: number | null) =>
  n == null ? "n/a" : n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const signedR = (r: number) => `${r >= 0 ? "+" : ""}${r.toFixed(2)}R`;

function utc(iso: string | null) {
  if (!iso) return null;
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" }),
    time: d.toISOString().slice(11, 16),
    weekday: d.toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" }),
    hour: d.getUTCHours() + d.getUTCMinutes() / 60,
  };
}

function session(hour: number) {
  if (hour < 7) return "the Asian session";
  if (hour < 12) return "the London session";
  if (hour < 16) return "the London and New York overlap";
  if (hour < 21) return "the New York session";
  return "the late New York session";
}

function duration(a: string | null, b: string) {
  if (!a) return null;
  const mins = Math.max(0, Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60000));
  if (mins < 60) return `${mins} minutes`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} hours ${m} minutes` : `${h} hours`;
}

export type CaseStudyEditorial = {
  isArchive: boolean;
  label: string;
  h1: string;
  title: string;
  description: string;
  sections: { heading: string; body: string }[];
  facts: { q: string; a: string }[];
  disclosure: string;
};

export function buildCaseStudyEditorial(t: CaseStudySummary, netR: number): CaseStudyEditorial {
  const isArchive = t.source === "Verified Archive";
  const dir = t.side === "Sell" ? "Short" : "Long";
  const opened = utc(t.openedAt);
  const dateStr = opened?.date ?? utc(t.closedAt)!.date;
  const label = isArchive ? "Historical strategy replay" : "Recorded live desk decision";
  const outcome =
    t.result === "Target Achieved"
      ? "second target reached"
      : t.result === "Partial + Risk-Free"
        ? "first target reached, remainder closed at break even"
        : t.result === "Risk Invalidation"
          ? "stop reached, risk invalidated"
          : t.result.toLowerCase();
  const risk = t.entry != null && t.sl != null ? Math.abs(t.entry - t.sl) : null;
  const dur = duration(t.openedAt, t.closedAt);
  const sess = opened ? session(opened.hour) : null;
  const towards = t.side === "Sell" ? "below" : "above";
  const against = t.side === "Sell" ? "above" : "below";

  const h1 = `XAUUSD ${dir} Case Study at ${fmt(t.entry)}: ${dateStr}`;
  const title = `XAUUSD ${dir} at ${fmt(t.entry)} (${dateStr}) ${signedR(netR)} | Gold Case Study`;
  const description = `${label}: XAUUSD ${dir.toLowerCase()} at ${fmt(t.entry)} on ${dateStr}, stop ${fmt(t.sl)}, targets ${fmt(t.tp1)} and ${fmt(t.tp2)}. Outcome ${signedR(netR)}, ${outcome}. Candle by candle replay.`;

  const sections = [
    {
      heading: "Record and timing",
      body: isArchive
        ? `This decision comes from a historical replay of the current rule set over recorded M5 gold prices. It was not published live at the time. The setup opened on ${opened?.weekday ?? ""} ${dateStr} at ${opened?.time ?? "n/a"} UTC${dur ? ` and resolved after ${dur} of market time` : ""}.`
        : `This decision was recorded by the Orion Live Desk on ${opened?.weekday ?? ""} ${dateStr} at ${opened?.time ?? "n/a"} UTC and kept exactly as it traded${dur ? `. It resolved after ${dur}` : ""}. Levels were not edited after publication.`,
    },
    {
      heading: "Market context",
      body: `The ${dir.toLowerCase()} entry formed during ${sess ?? "an active session"}, aligned with the prevailing higher timeframe direction used by the desk. Gold liquidity is deepest across London and New York, which is why most qualified decisions cluster in those hours.`,
    },
    {
      heading: "Liquidity and structure",
      body: `The decision was taken after a pullback against the prevailing move, with the expectation that price would resume ${t.side === "Sell" ? "lower" : "higher"} toward resting liquidity ${towards} the entry. Structure ${against} the stop at ${fmt(t.sl)} defined where the idea would no longer hold.`,
    },
    {
      heading: "Entry and risk",
      body: `Entry ${fmt(t.entry)}, stop ${fmt(t.sl)}${risk != null ? ` (risk of $${fmt(risk)} per ounce, defined as 1R)` : ""}, first target ${fmt(t.tp1)} and second target ${fmt(t.tp2)}. Risk was fixed before entry and was not widened during the trade.`,
    },
    {
      heading: "Management and outcome",
      body: `Half of the position is closed at the first target and the stop moves to break even, leaving the remainder to work toward the second target. Result: ${outcome}, net ${signedR(netR)}. ${netR < 0 ? "A loss of exactly 1R reflects the risk accepted before entry, not a management error." : "The outcome follows the predefined plan without discretionary intervention."}`,
    },
  ];

  const dirWord = t.side === "Sell" ? "short" : "long";
  // Half the position closes at TP1, so its share of the result is half the TP1 distance in R.
  const tp1Share = risk && t.entry != null && t.tp1 != null ? `${(0.5 * Math.abs(t.tp1 - t.entry) / risk).toFixed(2)}R` : "1R";
  const riskStr = risk != null ? `$${fmt(risk)} per ounce` : "a fixed amount per ounce";
  const facts = [
    {
      q: `What was the outcome of this XAUUSD ${dirWord} trade on ${dateStr}?`,
      a: `The trade closed at ${signedR(netR)}: ${outcome}. Entry was ${fmt(t.entry)} with a stop at ${fmt(t.sl)} and targets at ${fmt(t.tp1)} and ${fmt(t.tp2)}. Risk was fixed before entry and never widened during the trade.`,
    },
    {
      q: `What risk to reward structure did this gold trade use?`,
      a: `Risk was ${riskStr}, defined as 1R before entry. The first target paid ${tp1Share} on half the position and moved the stop to break even, and the remainder worked toward the second target. The result was ${signedR(netR)}.`,
    },
    {
      q: `When was this XAUUSD trade opened and how long did it last?`,
      a: `It opened on ${opened?.weekday ?? ""} ${dateStr} at ${opened?.time ?? "n/a"} UTC during ${sess ?? "an active session"}${dur ? ` and resolved after ${dur} of market time` : ""}. Gold liquidity is deepest across London and New York, where most qualified decisions cluster.`,
    },
    {
      q: `How was this gold trade managed after entry?`,
      a: `Half of the position was closed at the first target ${fmt(t.tp1)} and the stop moved to break even, leaving the remainder to work toward ${fmt(t.tp2)}. Management followed the predefined plan without discretionary intervention.`,
    },
    {
      q: isArchive
        ? `Is this XAUUSD case study a live signal or a historical replay?`
        : `Was this XAUUSD trade recorded live?`,
      a: isArchive
        ? `It is a historical replay of the current rule set over recorded M5 gold prices, not a signal published live at the time. It is shown for educational and analytical purposes and is not financial advice.`
        : `Yes. It was recorded by the Orion Live Desk on ${dateStr} and kept exactly as it traded, with levels unedited after publication. It is shown for educational purposes and is not financial advice.`,
    },
  ];

  const disclosure = isArchive
    ? "Historical strategy replay for educational and analytical purposes. It was not a live published signal. Not financial or investment advice. Past results do not imply future outcomes."
    : "Recorded software decision shown for educational and analytical purposes. Not financial or investment advice. Past results do not imply future outcomes.";

  return { isArchive, label, h1, title, description, sections, facts, disclosure };
}
